import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Panel } from "@/components/mh/bits";
import { DeptHeader, DeptFilters, RoleHover, StageColumns } from "@/components/mh/DeptParts";
import { TailoredBrief } from "@/components/mh/TailoredBrief";
import { buildDeptSky } from "@/components/mh/DeptSky";
import { deptLayout, linkIndex, roleStatus, deptTotals, mid, isTouch } from "@/lib/sky";

const useMobile = () => {
  const q = "(max-width: 639px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => { const mq = window.matchMedia(q), f = () => setM(mq.matches); mq.addEventListener("change", f); return () => mq.removeEventListener("change", f); }, []);
  return m;
};

export function DeptView({ d, agents, needsDept, onOpen, back, panelOpen, search, profile }) {
  const [sp] = useSearchParams();
  const sel = sp.get("role");
  const mobile = useMobile();
  const [f, setF] = useState({ wave: "all", status: "all", needsOnly: false });
  const [mode, setModeS] = useState(() => localStorage.getItem("mh-dept-mode") || "sky");
  const setMode = (m) => { localStorage.setItem("mh-dept-mode", m); setModeS(m); };
  const [hover, setHover] = useState(null);
  const [tapped, setTapped] = useState(null);
  const list = useMemo(() => agents.filter((a) => a.department === d.slug).map((a) => ({ ...a, hex: d.hex })), [agents, d]);
  const { nodes, labels } = useMemo(() => deptLayout(list), [list]);
  const idx = useMemo(() => linkIndex(agents), [agents]);
  const inDept = useMemo(() => new Set(list.map((a) => a.slug)), [list]);
  const status = (a) => roleStatus(a, needsDept);
  const tot = deptTotals(list, status);
  const maxMid = Math.max(1, ...list.map(mid));
  const radius = (n) => (tot.tailored && n.tailored?.enabled ? 20 + 9 * Math.sqrt(mid(n) / maxMid) : 25);
  const match = (a) => (f.wave === "all" || (a.tailored?.enabled && a.tailored.wave === Number(f.wave))) && (f.status === "all" || status(a) === f.status) && (!f.needsOnly || status(a) === "needs");
  const linked = (s) => [...idx.req(s), ...idx.en(s), ...idx.rel(s)];
  const outside = (s) => new Set(linked(s).filter((x) => !inDept.has(x))).size;
  const lit = hover ? new Set([hover.slug, ...linked(hover.slug)]) : null;

  useEffect(() => {
    const k = (e) => { if (e.key === "Escape" && !panelOpen) back(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [back, panelOpen]);
  useEffect(() => { if (sel) document.getElementById(`role-card-${sel}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [sel, mode, list.length]);

  const clickNode = (n) => { if (isTouch() && tapped !== n.slug) { setTapped(n.slug); setHover(n); return; } onOpen(n.slug); };
  const showSky = mode === "sky" && !mobile;
  const p = { status, lit, sel, onOpen, setHover, hex: d.hex, outside };

  return (
    <div className="space-y-6" data-testid={`dept-view-${d.slug}`}>
      <DeptHeader d={d} tot={tot} back={back} search={search} />
      <TailoredBrief d={d} profile={profile} list={list} tot={tot} />
      <DeptFilters f={f} setF={setF} tailored={tot.tailored} mode={mode} setMode={setMode} mobile={mobile} />
      {showSky ? (
        <Panel cls="relative overflow-hidden aspect-[16/9] max-h-[88vh] w-full bg-[#030305] select-none" testId="dept-sky">
          {buildDeptSky({ dept: d, nodes, labels, idx, hover: hover?.slug, sel, lit, match, status, radius, onHover: setHover, onNode: clickNode })}
          {hover && <RoleHover n={hover} d={d} st={status(hover)} outside={outside(hover.slug)} hint={tapped === hover.slug && isTouch()} />}
        </Panel>
      ) : <StageColumns list={list.filter(match)} p={p} />}
    </div>
  );
}
